/**
 * Falcon error tracking for dogfooding.
 *
 * Reports errors from the Falcon frontend to the Falcon backend using the official SDK.
 * This is used to "eat our own dogfood" - Falcon tracking Falcon.
 */

// Singleton instance (lazy initialization)
let falconInstance: import('@roselabs-io/falcon').Falcon | null = null

/**
 * Get the Falcon SDK instance.
 */
export function getFalconInstance(): import('@roselabs-io/falcon').Falcon | null {
  return falconInstance
}

/**
 * Initialize Falcon error tracking from environment variables.
 *
 * Call this once at app startup (e.g., in main.tsx):
 *
 *     import { initFalconTracker } from './lib/falconTracker'
 *     initFalconTracker()
 *
 * Environment variables (set in .env):
 *     VITE_FALCON_API_KEY: Your Falcon API key (fk_...)
 *     VITE_FALCON_API_URL: Falcon API URL (or falls back to VITE_API_URL)
 *     VITE_APP_VERSION: App version for release tracking
 */
export async function initFalconTracker(): Promise<import('@roselabs-io/falcon').Falcon | null> {
  if (falconInstance) {
    return falconInstance
  }

  const apiKey = import.meta.env.VITE_FALCON_API_KEY as string | undefined
  const apiUrl = (import.meta.env.VITE_FALCON_API_URL || import.meta.env.VITE_API_URL) as
    | string
    | undefined
  const environment = (import.meta.env.MODE || 'development') as string
  const release = import.meta.env.VITE_APP_VERSION as string | undefined

  // Only initialize if API key is provided
  if (!apiKey || !apiUrl) {
    return null
  }

  // Skip in test environment
  if (import.meta.env.MODE === 'test') {
    return null
  }

  try {
    const { Falcon } = await import('@roselabs-io/falcon')

    falconInstance = new Falcon({
      apiKey,
      appName: 'falcon-frontend',
      apiUrl,
      environment,
      release,
      debug: false,
      // Rate limiting to prevent self-DoS
      maxEventsPerSecond: 5,
      maxEventsPerMinute: 30,
    })

    // Install global error handlers (window.error, unhandledrejection)
    falconInstance.install()

    return falconInstance
  } catch {
    return null
  }
}

/**
 * Capture an error from React ErrorBoundary.
 */
export function captureReactError(
  error: Error,
  errorInfo: { componentStack?: string | null }
): void {
  if (!falconInstance) return

  falconInstance.captureException(error, {
    context: {
      componentStack: errorInfo.componentStack ?? '',
    },
    level: 'error',
  })
}

/**
 * Capture an error manually.
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (!falconInstance) return
  falconInstance.captureException(error, { context })
}

/**
 * Capture a message manually.
 */
export function captureMessage(
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' | 'fatal' = 'info',
  context?: Record<string, unknown>
): void {
  if (!falconInstance) return
  falconInstance.captureMessage(message, level, { context })
}

/**
 * Set user context for error tracking.
 */
export function setUser(user: { id?: string; email?: string; name?: string } | null): void {
  if (!falconInstance) return
  falconInstance.setUser(user)
}

/**
 * Check if tracking is enabled.
 */
export function isEnabled(): boolean {
  return falconInstance !== null
}

// Re-export the falconTracker singleton for backwards compatibility
export const falconTracker = {
  init: initFalconTracker,
  captureError,
  captureReactError,
  captureMessage,
  setUser,
  isEnabled,
}

// Initialize on module load (fire and forget)
initFalconTracker()
